import { z } from 'zod';
import { IEcgExaminationWorklistDTO } from '../models/ecg';
import { IdDescriptionItemSchema } from './iworklist.validation';

// Schema for IdDescriptionItemEcg (extends IdDescriptionItem with any id)
export const IdDescriptionItemEcgSchema = z
    .object({
        id: z.any(), // EcgExamination uses 'any' for id fields
        description: z.string(),
    })
    .strict();

// Schema for BusinessUnit (ECG-specific)
export const EcgBusinessUnitSchema = z
    .object({
        id: z.any(),
        description: z.string().nullable(),
        code: z.number(),
        httpPacs: z.string().nullable(),
        ecgHeader: z.string().nullable(),
        ecgVersioningText: z.string().nullable(),
        ecgSignatureText: z.string().nullable(),
        ecgLogoImgName: z.string().nullable(),
    })
    .strict();

// Schema for EcgProvenance
export const EcgProvenanceSchema = z
    .object({
        id: z.any(),
        description: z.string().nullable(),
        groupProvenance: IdDescriptionItemEcgSchema,
    })
    .strict();

// Schema for ICommandVisitExamination (from visit models)
const ICommandVisitExaminationSchema = z
    .object({
        canBeDeferredAndDelete: z.boolean().optional(),
        canBeSuspended: z.boolean(),
        canBeUndoSuspended: z.boolean(),
        canEditExam: z.boolean().optional(),
        isMandatoryEquipmentChange: z.boolean().optional(),
        canBeResigned: z.boolean(),
    })
    .strict();

// Schema for EcgPatient (extends BasePatient with ECG-specific properties)
export const EcgPatientSchema = z
    .object({
        id: z.any(), // ECG uses 'any' for id
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),

        birthDate: z.any(), // ECG uses 'any' for birthDate
        sex: z.string().nullable(),
        idLocalPatient: z.string().nullable(),
        isLocalPatient: z.boolean(),
        sexCode: z.string().nullable(),
        taxCode: z.string().nullable(), // Required in EcgPatient, optional in base
        // ECG-specific properties
        isCertificating: z.boolean().optional().nullable(),
        patientId: z.string(),
        // Missing properties from ICommonWorklistPatientDTO
        race: z.string().nullable(),
        otherPatientId: z.string(),
        residenceCity: z.string().nullable(),
        issuer: z.string().nullable(),
    })
    .strict();

// Schema for Exam (from vnext.models)
export const ExamSchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        isMonitoring: z.boolean().optional(),
        isRemoteVisit: z.boolean().optional(),
        otherCode: z.string().optional(),
    })
    .strict();

// Schema for Severity (from vnext.models)
const SeveritySchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        color: z.string().nullable(),
    })
    .strict();

// Schema for User (from vnext.models)
export const UserSchema = z
    .object({
        id: z.any(),
        fullName: z.string().nullable(),
        employeeSerialNumber: z.string().optional().nullable(),
        userName: z.string().optional().nullable(),
    })
    .strict();

// Complete schema for EcgExamination with all required properties
export const EcgExaminationSchema = z
    .object({
        id: z.string(),
        exam: ExamSchema,
        isMerged: z.boolean(),
        referringPhysician: UserSchema.nullable(),
        reportDate: z.string().nullable(),
        severity: SeveritySchema.nullable(),
        status: z.string().nullable(),
        idReport: z.string().nullable(),
        idLockedUser: z.string().nullable(),
        lockedHost: z.string().nullable(),
        lockedUserFullName: z.string().nullable(),

        examinationDate: z.string().nullable(),
        examinationsNumber: z.number().optional(),
        sameStudyInstanceNumber: z.number().optional(),
        isAdmitted: z.boolean(),
        isApproved: z.boolean(),
        isDigitallySigned: z.boolean(),
        isEcgReport: z.boolean(),
        isExecuted: z.boolean(),
        isReported: z.boolean(),
        imageAvailability: z.boolean(),
        locked: z.boolean(),
        examNumber: z.string().nullable(),

        // EcgExamination-specific properties (overrides and additions)
        reportingWithoutTrace: z.boolean(),
        patient: EcgPatientSchema, // Overrides BaseExamination.patient
        businessUnit: EcgBusinessUnitSchema, // ECG-specific BusinessUnit
        provenance: EcgProvenanceSchema, // ECG-specific Provenance
        command: ICommandVisitExaminationSchema.optional(), // Optional command

        // Additional properties to match requirements
        equipment: IdDescriptionItemSchema.optional(),
        diagnosis: IdDescriptionItemSchema.nullable(),
        admissionDate: z.string().nullable().optional(),
        executionDate: z.string().nullable().optional(),
        addendumNumber: z.number().optional(),
        fromIntegration: z.boolean().optional(),
        accessionNumber: z.string().nullable().optional(),
        revaluedCDA: z.boolean().nullable().optional(),
    })
    .strict();

export const validateEcgExaminationDTO = (data: unknown): IEcgExaminationWorklistDTO => {
    return EcgExaminationSchema.parse(data) as IEcgExaminationWorklistDTO;
};

export const applyEcgExaminationValidation = (data: unknown, index: number) => {
    try {
        validateEcgExaminationDTO(data);
    } catch (error: any) {
        console.error(`Validation failed for examination at index ${index}:`, error.errors);
        throw new Error(`Invalid examination data at index ${index}: ${error.message}`);
    }
};
